import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Svg,
  Rect,
} from '@react-pdf/renderer';
import type {
  RelatorioFinanceiro,
  RelatorioInadimplentes,
} from '../types/relatorios';

type RelatorioPDFProps = {
  relatorio: RelatorioFinanceiro | null;
  inadimplentes: RelatorioInadimplentes | null;
  anoLetivoId: number | null;
  tipo?: 'completo' | 'financeiro' | 'inadimplentes';
};

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

const formatPercent = (value: number) => {
  return `${value.toFixed(1)}%`;
};

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('pt-BR');
};

const styles = StyleSheet.create({
  page: {
    padding: 24,
    fontSize: 10,
    color: '#2c3e50',
    fontFamily: 'Helvetica',
  },
  header: {
    marginBottom: 12,
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
  },
  subtitle: {
    fontSize: 10,
    marginTop: 2,
    color: '#5d6d7e',
  },
  versionTag: {
    marginTop: 6,
    fontSize: 8,
    color: '#95a5a6',
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    marginTop: 12,
    marginBottom: 6,
    color: '#1f2d3d',
  },
  highlights: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  highlightCard: {
    width: '48%',
    borderRadius: 6,
    padding: 8,
    border: '1px solid #e6e8eb',
    marginBottom: 8,
    marginRight: 8,
    backgroundColor: '#f8f9fb',
  },
  highlightCardWide: {
    width: '100%',
  },
  highlightLabel: {
    fontSize: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    color: '#7f8c8d',
  },
  highlightValue: {
    fontSize: 13,
    fontWeight: 700,
    marginTop: 4,
  },
  highlightValueImportant: {
    color: '#c0392b',
  },
  table: {
    borderWidth: 1,
    borderColor: '#e6e8eb',
    borderRadius: 6,
    overflow: 'hidden',
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableHeader: {
    backgroundColor: '#f2f4f7',
  },
  tableCell: {
    padding: 4,
    borderRightWidth: 1,
    borderRightColor: '#e6e8eb',
    borderBottomWidth: 1,
    borderBottomColor: '#e6e8eb',
    fontSize: 8,
  },
  tableCellLast: {
    borderRightWidth: 0,
  },
  tableCellHeader: {
    fontWeight: 700,
    color: '#34495e',
    textTransform: 'uppercase',
  },
  textRight: {
    textAlign: 'right',
  },
  textCenter: {
    textAlign: 'center',
  },
  chartBlock: {
    borderWidth: 1,
    borderColor: '#e6e8eb',
    borderRadius: 6,
    padding: 8,
    marginBottom: 10,
  },
  chartTitle: {
    fontSize: 10,
    fontWeight: 700,
    marginBottom: 6,
  },
  legendRow: {
    flexDirection: 'row',
    marginTop: 6,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 12,
  },
  legendSwatch: {
    width: 8,
    height: 8,
    marginRight: 4,
  },
  badge: {
    fontSize: 8,
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 10,
    backgroundColor: '#eaf2f8',
    color: '#2e86c1',
  },
  caseHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  caseTitle: {
    fontSize: 12,
    fontWeight: 700,
  },
  caseTotal: {
    fontSize: 12,
    fontWeight: 700,
    color: '#c0392b',
  },
  detailGrid: {
    flexDirection: 'row',
  },
  detailCol: {
    flex: 1,
    paddingRight: 12,
  },
  detailColLast: {
    flex: 1,
    paddingRight: 0,
  },
  detailLabel: {
    fontSize: 8,
    textTransform: 'uppercase',
    color: '#7f8c8d',
    marginTop: 6,
  },
  detailValue: {
    fontSize: 9,
    marginTop: 2,
  },
});

const StackedBarChart = ({ meses }: { meses: RelatorioFinanceiro['meses'] }) => {
  const chartWidth = 520;
  const chartHeight = 110;
  const barHeight = 70;
  const barTop = 10;
  const gap = 4;
  const barCount = meses.length || 1;
  const barWidth = (chartWidth - gap * (barCount - 1)) / barCount;
  const maxTotal = Math.max(
    ...meses.map((m) => m.recebido + m.pendente + m.atrasado),
    1,
  );

  return (
    <Svg width={chartWidth} height={chartHeight}>
      {meses.map((m, index) => {
        const recebidoH = barHeight * (m.recebido / maxTotal);
        const pendenteH = barHeight * (m.pendente / maxTotal);
        const atrasadoH = barHeight * (m.atrasado / maxTotal);
        const x = index * (barWidth + gap);
        const yBase = barTop + barHeight;

        return (
          <React.Fragment key={`${m.chave}-${index}`}>
            <Rect x={x} y={yBase - recebidoH} width={barWidth} height={recebidoH} fill="#2ecc71" />
            <Rect x={x} y={yBase - recebidoH - pendenteH} width={barWidth} height={pendenteH} fill="#f1c40f" />
            <Rect x={x} y={yBase - recebidoH - pendenteH - atrasadoH} width={barWidth} height={atrasadoH} fill="#e74c3c" />
          </React.Fragment>
        );
      })}
    </Svg>
  );
};

const InadimplenciaChart = ({ meses }: { meses: RelatorioFinanceiro['meses'] }) => {
  const chartWidth = 520;
  const chartHeight = 90;
  const barHeight = 50;
  const barTop = 10;
  const gap = 4;
  const barCount = meses.length || 1;
  const barWidth = (chartWidth - gap * (barCount - 1)) / barCount;
  const maxValue = Math.max(...meses.map((m) => m.inadimplencia), 1);

  return (
    <Svg width={chartWidth} height={chartHeight}>
      {meses.map((m, index) => {
        const h = barHeight * (m.inadimplencia / maxValue);
        const x = index * (barWidth + gap);
        const yBase = barTop + barHeight;

        return (
          <Rect
            key={`${m.chave}-${index}`}
            x={x}
            y={yBase - h}
            width={barWidth}
            height={h}
            fill="#8e44ad"
          />
        );
      })}
    </Svg>
  );
};

const RelatorioPDF: React.FC<RelatorioPDFProps> = ({ relatorio, inadimplentes, anoLetivoId, tipo = 'completo' }) => {
  const generatedAt = new Date();
  const showFinanceiro = tipo === 'completo' || tipo === 'financeiro';
  const showInadimplentes = tipo === 'completo' || tipo === 'inadimplentes';

  const highlights = [];

  if (showFinanceiro && relatorio) {
    highlights.push(
      { label: 'Total esperado', value: formatCurrency(relatorio.totais.esperado) },
      { label: 'Total recebido', value: formatCurrency(relatorio.totais.recebido) },
      { label: 'Total faturado', value: formatCurrency(relatorio.totais.faturado) },
      { label: 'Total pendente', value: formatCurrency(relatorio.totais.pendente) },
      { label: 'Total em atraso', value: formatCurrency(relatorio.totais.atrasado), important: true },
      { label: 'Inadimplencia', value: formatPercent(relatorio.totais.inadimplencia), important: true }
    );
  }

  if (showInadimplentes && inadimplentes) {
    highlights.push({
      label: 'Casos inadimplentes',
      value: String(inadimplentes.totais.totalCasos),
    });
    highlights.push({
      label: 'Total atrasado (inadimplentes)',
      value: formatCurrency(inadimplentes.totais.totalAtrasado),
      important: true,
    });
  }

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.title}>
            {tipo === 'completo' ? 'Relatorio Financeiro e Inadimplentes' : tipo === 'financeiro' ? 'Relatorio Financeiro' : 'Relatorio de Inadimplentes'}
          </Text>
          <Text style={styles.subtitle}>
            Gerado em {generatedAt.toLocaleString('pt-BR')} {anoLetivoId ? `• Ano letivo ${anoLetivoId}` : '• Todos os anos'}
          </Text>
          <Text style={styles.versionTag}>
            Layout PDF v2
          </Text>
        </View>

        <Text style={styles.sectionTitle}>Destaques</Text>
        <View style={styles.highlights}>
          {highlights.map((item, index) => (
            <View
              key={`${item.label}-${index}`}
              style={index % 2 === 1 ? [styles.highlightCard, { marginRight: 0 }] : styles.highlightCard}
            >
              <Text style={styles.highlightLabel}>{item.label}</Text>
              <Text style={item.important ? [styles.highlightValue, styles.highlightValueImportant] : styles.highlightValue}>
                {item.value}
              </Text>
            </View>
          ))}
          {showFinanceiro && relatorio && (
            <View style={[styles.highlightCard, styles.highlightCardWide, { marginRight: 0 }]}>
              <Text style={styles.highlightLabel}>Lancamentos</Text>
              <Text style={styles.highlightValue}>
                {relatorio.totais.totalLancamentos} lancamentos • {relatorio.totais.lancamentosPagos} pagos • {relatorio.totais.lancamentosAtrasados} atrasados
              </Text>
            </View>
          )}
        </View>

        {showFinanceiro && relatorio && (
          <>
            <Text style={styles.sectionTitle}>Graficos</Text>
            <View style={styles.chartBlock}>
              <Text style={styles.chartTitle}>Evolucao mensal (recebido/pendente/atrasado)</Text>
              <StackedBarChart meses={relatorio.meses} />
              <View style={styles.legendRow}>
                <View style={styles.legendItem}>
                  <View style={[styles.legendSwatch, { backgroundColor: '#2ecc71' }]} />
                  <Text>Recebido</Text>
                </View>
                <View style={styles.legendItem}>
                  <View style={[styles.legendSwatch, { backgroundColor: '#f1c40f' }]} />
                  <Text>Pendente</Text>
                </View>
                <View style={styles.legendItem}>
                  <View style={[styles.legendSwatch, { backgroundColor: '#e74c3c' }]} />
                  <Text>Atrasado</Text>
                </View>
              </View>
            </View>

            <View style={styles.chartBlock}>
              <Text style={styles.chartTitle}>Inadimplencia por mes</Text>
              <InadimplenciaChart meses={relatorio.meses} />
            </View>
          </>
        )}
      </Page>

      {showFinanceiro && relatorio && (
        <Page size="A4" style={styles.page}>
          <Text style={styles.sectionTitle}>Detalhamento por mes</Text>
          <View style={styles.table}>
            <View style={[styles.tableRow, styles.tableHeader]}>
              <Text style={[styles.tableCell, styles.tableCellHeader, { flex: 1.2 }]}>Mes/Ano</Text>
              <Text style={[styles.tableCell, styles.tableCellHeader, styles.textRight, { flex: 1 }]}>Esperado</Text>
              <Text style={[styles.tableCell, styles.tableCellHeader, styles.textRight, { flex: 1 }]}>Recebido</Text>
              <Text style={[styles.tableCell, styles.tableCellHeader, styles.textRight, { flex: 1 }]}>Faturado</Text>
              <Text style={[styles.tableCell, styles.tableCellHeader, styles.textRight, { flex: 1 }]}>Pendente</Text>
              <Text style={[styles.tableCell, styles.tableCellHeader, styles.textRight, { flex: 1 }]}>Atrasado</Text>
              <Text style={[styles.tableCell, styles.tableCellHeader, styles.textRight, { flex: 1 }]}>Inad.</Text>
              <Text style={[styles.tableCell, styles.tableCellHeader, styles.textRight, styles.tableCellLast, { flex: 1 }]}>Total</Text>
            </View>
            {relatorio.meses.map((mes) => {
              const total = mes.recebido + mes.pendente + mes.atrasado;
              return (
                <View key={mes.chave} style={styles.tableRow}>
                  <Text style={[styles.tableCell, { flex: 1.2 }]}>{mes.mes} {mes.ano}</Text>
                  <Text style={[styles.tableCell, styles.textRight, { flex: 1 }]}>{formatCurrency(mes.esperado)}</Text>
                  <Text style={[styles.tableCell, styles.textRight, { flex: 1 }]}>{formatCurrency(mes.recebido)}</Text>
                  <Text style={[styles.tableCell, styles.textRight, { flex: 1 }]}>{formatCurrency(mes.faturado)}</Text>
                  <Text style={[styles.tableCell, styles.textRight, { flex: 1 }]}>{formatCurrency(mes.pendente)}</Text>
                  <Text style={[styles.tableCell, styles.textRight, { flex: 1 }]}>{formatCurrency(mes.atrasado)}</Text>
                  <Text style={[styles.tableCell, styles.textRight, { flex: 1 }]}>{formatPercent(mes.inadimplencia)}</Text>
                  <Text style={[styles.tableCell, styles.textRight, styles.tableCellLast, { flex: 1 }]}>{formatCurrency(total)}</Text>
                </View>
              );
            })}
          </View>
        </Page>
      )}

      {showInadimplentes && inadimplentes && (
        <Page size="A4" style={styles.page}>
          <Text style={styles.sectionTitle}>Inadimplentes por responsavel/aluno</Text>
          <View style={styles.table}>
            <View style={[styles.tableRow, styles.tableHeader]}>
              <Text style={[styles.tableCell, styles.tableCellHeader, { flex: 1.4 }]}>Responsavel</Text>
              <Text style={[styles.tableCell, styles.tableCellHeader, { flex: 1.2 }]}>Aluno</Text>
              <Text style={[styles.tableCell, styles.tableCellHeader, { flex: 1 }]}>Turma</Text>
              <Text style={[styles.tableCell, styles.tableCellHeader, { flex: 1.1 }]}>Meses</Text>
              <Text style={[styles.tableCell, styles.tableCellHeader, styles.textRight, { flex: 0.8 }]}>Total</Text>
              <Text style={[styles.tableCell, styles.tableCellHeader, styles.tableCellLast, { flex: 1.5 }]}>Valores por mes</Text>
            </View>
            {inadimplentes.resumo.map((linha, index) => (
              <View key={`${linha.responsavelId}-${linha.alunoId}-${index}`} style={styles.tableRow}>
                <Text style={[styles.tableCell, { flex: 1.4 }]}>{linha.responsavelNome}</Text>
                <Text style={[styles.tableCell, { flex: 1.2 }]}>{linha.alunoNome}</Text>
                <Text style={[styles.tableCell, { flex: 1 }]}>{linha.turmaNome || '-'}</Text>
                <Text style={[styles.tableCell, { flex: 1.1 }]}>{linha.meses.join(', ') || '-'}</Text>
                <Text style={[styles.tableCell, styles.textRight, { flex: 0.8 }]}>{formatCurrency(linha.totalAtrasado)}</Text>
                <Text style={[styles.tableCell, styles.tableCellLast, { flex: 1.5 }]}>
                  {linha.valoresPorMes.map((m) => `${m.mes}: ${formatCurrency(m.valor)}`).join(' | ')}
                </Text>
              </View>
            ))}
          </View>
        </Page>
      )}

      {showInadimplentes && inadimplentes?.casos.map((caso, index) => (
        <Page key={`caso-${index}`} size="A4" style={styles.page}>
          <View style={styles.caseHeader}>
            <View>
              <Text style={styles.caseTitle}>Caso {index + 1} - Judicializacao</Text>
              <Text style={styles.subtitle}>
                {caso.responsavel?.nome || caso.responsavel?.razaoSocial || 'Responsavel nao informado'}
                {' '}•{' '}
                {caso.aluno?.nome || 'Aluno nao informado'}
                {caso.turma?.nome ? ` • ${caso.turma.nome}` : ''}
              </Text>
            </View>
            <Text style={styles.caseTotal}>{formatCurrency(caso.totalAtrasado)}</Text>
          </View>

          <View style={styles.detailGrid}>
            <View style={styles.detailCol}>
              <Text style={styles.detailLabel}>Responsavel</Text>
              <Text style={styles.detailValue}>Nome: {caso.responsavel?.nome || '-'}</Text>
              <Text style={styles.detailValue}>Razao social: {caso.responsavel?.razaoSocial || '-'}</Text>
              <Text style={styles.detailValue}>CPF/CNPJ: {caso.responsavel?.cpfCnpj || '-'}</Text>
              <Text style={styles.detailValue}>Email: {caso.responsavel?.email || '-'}</Text>
              <Text style={styles.detailValue}>Telefone: {caso.responsavel?.telefone || '-'}</Text>
              <Text style={styles.detailValue}>
                Endereco: {caso.responsavel?.endereco?.endereco || '-'} {caso.responsavel?.endereco?.numero || ''}
              </Text>
              <Text style={styles.detailValue}>
                Cidade/UF: {caso.responsavel?.endereco?.cidade || '-'} / {caso.responsavel?.endereco?.estado || '-'}
              </Text>
              <Text style={styles.detailValue}>CEP: {caso.responsavel?.endereco?.cep || '-'}</Text>
            </View>

            <View style={styles.detailColLast}>
              <Text style={styles.detailLabel}>Aluno</Text>
              <Text style={styles.detailValue}>Nome: {caso.aluno?.nome || '-'}</Text>
              <Text style={styles.detailValue}>Matricula: {caso.aluno?.matricula || '-'}</Text>
              <Text style={styles.detailValue}>CPF: {caso.aluno?.cpf || '-'}</Text>
              <Text style={styles.detailValue}>RG: {caso.aluno?.rg || '-'}</Text>
              <Text style={styles.detailValue}>Nascimento: {formatDate(caso.aluno?.dataNascimento)}</Text>
              <Text style={styles.detailLabel}>Contrato</Text>
              <Text style={styles.detailValue}>Numero: {caso.contrato?.numeroSequencial ?? '-'}</Text>
              <Text style={styles.detailValue}>Dia vencimento: {caso.contrato?.diaVencimento ?? '-'}</Text>
              <Text style={styles.detailValue}>
                Valor mensal: {caso.contrato?.valorMensal != null ? formatCurrency(Number(caso.contrato.valorMensal)) : '-'}
              </Text>
              <Text style={styles.detailValue}>
                Desconto mensal: {caso.contrato?.percentualDescontoMensal != null ? `${caso.contrato.percentualDescontoMensal}%` : '-'}
              </Text>
            </View>
          </View>

          <Text style={styles.sectionTitle}>Meses em atraso</Text>
          <View style={styles.table}>
            <View style={[styles.tableRow, styles.tableHeader]}>
              <Text style={[styles.tableCell, styles.tableCellHeader, { flex: 1.2 }]}>Mes</Text>
              <Text style={[styles.tableCell, styles.tableCellHeader, styles.textRight, styles.tableCellLast, { flex: 1 }]}>Valor</Text>
            </View>
            {caso.valoresPorMes.map((m) => (
              <View key={m.chave} style={styles.tableRow}>
                <Text style={[styles.tableCell, { flex: 1.2 }]}>{m.mes}</Text>
                <Text style={[styles.tableCell, styles.textRight, styles.tableCellLast, { flex: 1 }]}>{formatCurrency(m.valor)}</Text>
              </View>
            ))}
          </View>

          <Text style={styles.sectionTitle}>Lancamentos</Text>
          <View style={styles.table}>
            <View style={[styles.tableRow, styles.tableHeader]}>
              <Text style={[styles.tableCell, styles.tableCellHeader, { flex: 2 }]}>Descricao</Text>
              <Text style={[styles.tableCell, styles.tableCellHeader, { flex: 1 }]}>Vencimento</Text>
              <Text style={[styles.tableCell, styles.tableCellHeader, styles.textRight, { flex: 1 }]}>Valor</Text>
              <Text style={[styles.tableCell, styles.tableCellHeader, { flex: 1 }]}>Status</Text>
              <Text style={[styles.tableCell, styles.tableCellHeader, styles.tableCellLast, { flex: 1 }]}>Parcela</Text>
            </View>
            {caso.lancamentos.map((l) => (
              <View key={l.id} style={styles.tableRow}>
                <Text style={[styles.tableCell, { flex: 2 }]}>{l.descricao}</Text>
                <Text style={[styles.tableCell, { flex: 1 }]}>{formatDate(l.dataVencimento)}</Text>
                <Text style={[styles.tableCell, styles.textRight, { flex: 1 }]}>{formatCurrency(Number(l.valorFinal))}</Text>
                <Text style={[styles.tableCell, { flex: 1 }]}>{l.status}</Text>
                <Text style={[styles.tableCell, styles.tableCellLast, { flex: 1 }]}>{l.numeroParcela}/{l.totalParcelas}</Text>
              </View>
            ))}
          </View>
        </Page>
      ))}
    </Document>
  );
};

export default RelatorioPDF;
